import { useCallback, useRef } from "react"
import {
  useEditForm as useAppBaseEditForm,
  type UseEditFormOptions as AppBaseUseEditFormOptions,
} from "app-base/react/edit-form"
import { logger, fieldNames } from "./Logger"

// Shared edit-form plumbing extracted from the Cruise/Trip/Day/Ship edit views. Owns the
// useEditState triple, adapts the EditGroup (FormData, values) save signature, logs the
// save, and wires resetEdited. Simple views pass `update` (and optionally `create`/`isNew`
// for create-vs-update); views with bespoke branching (e.g. Ship) pass a full `save`
// override that receives `resetEdited`, plus an optional `validate`.

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Preserves existing untyped edit-view callback inference
export interface UseEditFormOptions<Values = any, Result = any>
  extends Omit<AppBaseUseEditFormOptions<Values, Result>, "onSaveStart"> {
  // Optional extra hook run whenever `onSaveError` fires with a real error (issue
  // #529) -- e.g. to log it, since `useEditForm` itself already exposes `saveError`
  // for rendering (typically via EditGroup's own `error` prop).
  onSaveError?: (error: unknown) => void;
  // Seeds `saveError` (issue #529) — for catalog/demo fixtures only; production
  // always starts with no error, only ever setting one via a real rejected `onSave`.
  initSaveError?: unknown;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Preserves existing untyped edit-view callback inference
export function useEditForm<Values = any, Result = any>(options: UseEditFormOptions<Values, Result>) {
  const revision = useRef(0);
  const { create, update, save, isNew } = options;
  // Use the shared hook's save seam so an acknowledgement cannot clear newer input.
  const saveCurrent = useCallback<NonNullable<typeof options.save>>(async (values, context) => {
    const savedRevision = revision.current;
    const resetEdited = () => {
      if (revision.current === savedRevision) context.resetEdited();
    };
    if (save) {
      await save(values, { resetEdited });
      return;
    }
    const operation = isNew && create ? create : update;
    if (!operation) throw new Error("useEditForm requires update, create, or save");
    if (await operation(values)) resetEdited();
  }, [create, update, save, isNew]);
  const form = useAppBaseEditForm({
    ...options,
    save: saveCurrent,
    onSaveStart: (values) => {
      logger.debug("saving", fieldNames(values));
    },
  });
  const onEdited = useCallback<typeof form.onEdited>((event) => {
    revision.current += 1;
    form.onEdited(event);
  }, [form.onEdited]);
  return { ...form, onEdited };
}
